import React from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import ToggleSwitch from '../../../components/shared/ToggleSwitch';
import {
  Search,
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Check,
  History,
  BarChart3,
  Zap,
  Cpu,
  Server,
  HardDrive,
  Car,
  Banknote,
  Gauge,
  TrendingUp,
  TrendingDown
} from 'lucide-react';

const MonitoringAnomalies = ({
  anomalyConfig,
  anomalyHistory,
  anomalyBaselines,
  checkingAnomalies,
  retrainingBaselines,
  savingAnomalyConfig,
  onCheckAnomalies,
  onRetrainBaselines,
  onSaveAnomalyConfig,
  onAcknowledgeAnomaly,
  updateAnomalyConfig,
  updateAnomalyMetric,
  getSeverityConfig
}) => {
  if (!anomalyConfig) return null;

  return (
    <div className="space-y-6">
      {/* Anomaly Detection Header */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Search className="h-5 w-5 text-purple-600" />
              Anomaly Detection
            </h3>
            <p className="text-sm text-gray-600">AI-powered detection of unusual patterns in system and business metrics</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={onRetrainBaselines}
              disabled={retrainingBaselines}
            >
              {retrainingBaselines ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Retrain Baselines
            </Button>
            <Button
              onClick={onCheckAnomalies}
              disabled={checkingAnomalies}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {checkingAnomalies ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Search className="h-4 w-4 mr-2" />
              )}
              Run Detection
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="p-4 bg-purple-50 rounded-lg">
            <p className="text-sm text-purple-600">Detection Status</p>
            <p className="text-xl font-bold text-purple-800">
              {anomalyConfig.enabled ? 'Active' : 'Disabled'}
            </p>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-600">Sensitivity</p>
            <p className="text-xl font-bold text-blue-800 capitalize">{anomalyConfig.sensitivity || 'Medium'}</p>
          </div>
          <div className="p-4 bg-orange-50 rounded-lg">
            <p className="text-sm text-orange-600">Total Detected</p>
            <p className="text-xl font-bold text-orange-800">{anomalyHistory.length}</p>
          </div>
          <div className="p-4 bg-red-50 rounded-lg">
            <p className="text-sm text-red-600">Unacknowledged</p>
            <p className="text-xl font-bold text-red-800">
              {anomalyHistory.filter(a => !a.acknowledged).length}
            </p>
          </div>
        </div>

        {/* Baseline Stats */}
        {anomalyBaselines?.baselines && Object.keys(anomalyBaselines.baselines).length > 0 && (
          <div className="mb-6">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-gray-600" />
              Learned Baselines
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {Object.entries(anomalyBaselines.baselines).map(([metric, stats]) => (
                <div key={metric} className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 truncate">{metric.replace(/_/g, ' ')}</p>
                  <p className="font-semibold">{stats.mean?.toFixed(1)} ± {stats.std?.toFixed(1)}</p>
                  <p className="text-xs text-gray-400">{stats.data_points} data points</p>
                </div>
              ))}
            </div>
            {anomalyBaselines.updated_at && (
              <p className="text-xs text-gray-400 mt-2">
                Last updated: {new Date(anomalyBaselines.updated_at).toLocaleString()}
              </p>
            )}
          </div>
        )}

        {Object.keys(anomalyBaselines?.baselines || {}).length === 0 && (
          <div className="p-4 bg-yellow-50 rounded-lg mb-6">
            <div className="flex items-center gap-2 text-yellow-800">
              <AlertTriangle className="h-5 w-5" />
              <p className="font-medium">Insufficient Data for Baselines</p>
            </div>
            <p className="text-sm text-yellow-700 mt-1">
              At least {anomalyConfig.min_data_points || 24} data points are needed to establish baselines. 
              The system will automatically collect metrics over time.
            </p>
          </div>
        )}
      </Card>

      {/* Anomaly Configuration */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-semibold">Detection Settings</h3>
            <p className="text-sm text-gray-600">Configure anomaly detection parameters</p>
          </div>
          <Button
            onClick={onSaveAnomalyConfig}
            disabled={savingAnomalyConfig}
            className="bg-pink-600 hover:bg-pink-700"
          >
            {savingAnomalyConfig ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>

        <div className="space-y-6">
          {/* Enable/Disable */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium">Enable Anomaly Detection</p>
              <p className="text-sm text-gray-600">Automatically detect unusual patterns in metrics</p>
            </div>
            <ToggleSwitch
              enabled={anomalyConfig.enabled}
              onToggle={() => updateAnomalyConfig('enabled', !anomalyConfig.enabled)}
              activeColor="bg-purple-600"
            />
          </div>

          {/* Sensitivity */}
          <div>
            <label className="font-medium">Sensitivity Level</label>
            <p className="text-sm text-gray-600 mb-2">Higher sensitivity detects smaller deviations</p>
            <div className="flex gap-3">
              {['low', 'medium', 'high'].map((level) => (
                <button
                  key={level}
                  onClick={() => updateAnomalyConfig('sensitivity', level)}
                  className={`px-4 py-2 rounded-lg border transition-colors ${
                    anomalyConfig.sensitivity === level
                      ? 'bg-purple-600 text-white border-purple-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400'
                  }`}
                >
                  <span className="capitalize">{level}</span>
                  <span className="text-xs block opacity-75">
                    {level === 'low' ? '3.0 σ' : level === 'medium' ? '2.5 σ' : '2.0 σ'}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Advanced Settings */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Baseline Window (hours)</label>
              <Input
                type="number"
                value={anomalyConfig.baseline_hours || 168}
                onChange={(e) => updateAnomalyConfig('baseline_hours', parseInt(e.target.value))}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Historical data used for baseline</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Min Data Points</label>
              <Input
                type="number"
                value={anomalyConfig.min_data_points || 24}
                onChange={(e) => updateAnomalyConfig('min_data_points', parseInt(e.target.value))}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Required for baseline calculation</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Cooldown (minutes)</label>
              <Input
                type="number"
                value={anomalyConfig.cooldown_minutes || 30}
                onChange={(e) => updateAnomalyConfig('cooldown_minutes', parseInt(e.target.value))}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Between same anomaly alerts</p>
            </div>
          </div>

          {/* Metrics to Monitor */}
          <div>
            <label className="font-medium">Metrics to Monitor</label>
            <p className="text-sm text-gray-600 mb-3">Select which metrics to analyze for anomalies</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { key: 'cpu', label: 'CPU Usage', icon: Cpu },
                { key: 'memory', label: 'Memory Usage', icon: Server },
                { key: 'disk', label: 'Disk Usage', icon: HardDrive },
                { key: 'rides_per_hour', label: 'Rides/Hour', icon: Car },
                { key: 'revenue_per_hour', label: 'Revenue/Hour', icon: Banknote },
                { key: 'response_time', label: 'Response Time', icon: Gauge }
              ].map(({ key, label, icon: Icon }) => (
                <div
                  key={key}
                  onClick={() => updateAnomalyMetric(key, !anomalyConfig.metrics_to_monitor?.[key])}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors flex items-center gap-3 ${
                    anomalyConfig.metrics_to_monitor?.[key]
                      ? 'bg-purple-50 border-purple-300'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <Icon className={`h-5 w-5 ${anomalyConfig.metrics_to_monitor?.[key] ? 'text-purple-600' : 'text-gray-400'}`} />
                  <span className={anomalyConfig.metrics_to_monitor?.[key] ? 'text-purple-800 font-medium' : 'text-gray-600'}>
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Anomaly History */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <History className="h-5 w-5 text-gray-600" />
          <h3 className="font-semibold">Detected Anomalies</h3>
          <Badge className="bg-purple-100 text-purple-800">{anomalyHistory.length} total</Badge>
        </div>
        
        {anomalyHistory.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Search className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No anomalies detected yet</p>
            <p className="text-sm">The system will automatically detect unusual patterns as data is collected</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {anomalyHistory.map((anomaly, idx) => {
              const isAbove = anomaly.direction === 'above';
              const severityConfig = getSeverityConfig(anomaly.severity);
              const Icon = isAbove ? TrendingUp : TrendingDown;
              
              return (
                <div 
                  key={anomaly.timestamp || idx} 
                  className={`p-4 rounded-lg border ${anomaly.acknowledged ? 'bg-gray-50 opacity-60' : severityConfig.bgColor} ${severityConfig.color.replace('bg-', 'border-').split(' ')[0]}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${severityConfig.color.split(' ')[0]}`}>
                        <Icon className={`h-5 w-5 ${severityConfig.color.split(' ')[1]}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={severityConfig.color}>{anomaly.severity}</Badge>
                          <Badge className="bg-gray-100 text-gray-700">{anomaly.metric_label}</Badge>
                        </div>
                        <p className="font-medium">{anomaly.message}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                          <span>Current: <strong>{anomaly.current_value}</strong></span>
                          <span>Expected: <strong>{anomaly.expected_value}</strong></span>
                          <span>Deviation: <strong>{anomaly.deviation_percent}%</strong></span>
                          <span>Z-Score: <strong>{anomaly.z_score}</strong></span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(anomaly.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    {!anomaly.acknowledged && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onAcknowledgeAnomaly(anomaly.timestamp)}
                        className="shrink-0"
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Acknowledge
                      </Button>
                    )}
                    {anomaly.acknowledged && (
                      <Badge className="bg-gray-200 text-gray-600">Acknowledged</Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* How it Works */}
      <Card className="p-6 bg-gradient-to-br from-purple-50 to-blue-50">
        <h4 className="font-semibold mb-3 flex items-center gap-2">
          <Zap className="h-5 w-5 text-purple-600" />
          How Anomaly Detection Works
        </h4>
        <div className="text-sm text-gray-700 space-y-2">
          <p>
            <strong>Z-Score Detection:</strong> The system calculates statistical baselines (mean and standard deviation) 
            from historical data and flags values that deviate significantly.
          </p>
          <p>
            <strong>Sensitivity Levels:</strong> Low (3.0σ) catches extreme outliers, Medium (2.5σ) is balanced, 
            High (2.0σ) detects smaller variations.
          </p>
          <p>
            <strong>Business-Aware:</strong> Rides and revenue baselines consider time-of-day patterns for more accurate detection.
          </p>
          <p>
            <strong>Integration:</strong> Detected anomalies trigger alerts via email, Slack, and Discord (if configured in Webhooks tab).
          </p>
        </div>
      </Card>
    </div>
  );
};

export default MonitoringAnomalies;
